import { type FeaturesDto, hasRole, type Role } from "@shelflist/shared";
import {
  BookOpen,
  Building2,
  Database,
  Import,
  Inbox,
  Layers,
  LayoutDashboard,
  Library,
  LibraryBig,
  ListChecks,
  type LucideIcon,
  Mail,
  PenLine,
  Send,
  Shield,
  Sparkles,
  Tag,
  Upload,
  UserRoundSearch,
  Users,
} from "lucide-react";

export type NavGroup = "reading" | "editing" | "admin";

export interface NavItem {
  /** Stable key (also used for test ids and badges). */
  key: string;
  /** Full translation key, e.g. `common:nav.library` or `admin:area.users`. */
  labelKey: string;
  to: string;
  minRole: Role;
  /** Path prefixes (on segment boundaries) for which the entry counts as active. */
  matchPrefixes: string[];
  group: NavGroup;
  icon: LucideIcon;
  /** Active only on exactly `to` (the library root). */
  exact?: boolean;
  /** Where the entry is shown: the inline header navigation, the mobile drawer, or both (default). */
  surface?: "inline" | "drawer";
  /** One of the Library section tabs (reached through the tabs on desktop, listed in the drawer). */
  libraryTab?: boolean;
  /** A page of the Administration area (sidebar and drawer). */
  adminPage?: boolean;
  /** Shown only while this feature flag (from `GET /api/settings/features`) is enabled. */
  feature?: NavFeature;
}

/** Feature flags that can gate navigation entries. */
export type NavFeature = "authorSuggestions";
export type NavFeatures = Partial<Pick<FeaturesDto, NavFeature>>;

const LIBRARY_PREFIXES = ["/books", "/authors", "/series", "/tags", "/publishers", "/suggestions"];

/** One definition for the inline navigation, the drawer and the admin sidebar, so they cannot drift apart. */
export const NAV_ITEMS: readonly NavItem[] = [
  {
    key: "library",
    labelKey: "common:nav.library",
    to: "/",
    minRole: "reader",
    matchPrefixes: LIBRARY_PREFIXES,
    group: "reading",
    exact: true,
    icon: LibraryBig,
  },
  {
    key: "shelves",
    labelKey: "common:nav.shelves",
    to: "/shelves",
    minRole: "reader",
    matchPrefixes: ["/shelves"],
    group: "reading",
    icon: Library,
  },
  {
    key: "reading",
    labelKey: "common:nav.reading",
    to: "/reading",
    minRole: "reader",
    matchPrefixes: ["/reading"],
    group: "reading",
    icon: BookOpen,
  },
  {
    key: "authors",
    labelKey: "common:nav.authors",
    to: "/authors",
    minRole: "reader",
    matchPrefixes: ["/authors"],
    group: "reading",
    icon: PenLine,
    surface: "drawer",
    libraryTab: true,
  },
  {
    key: "series",
    labelKey: "common:nav.series",
    to: "/series",
    minRole: "reader",
    matchPrefixes: ["/series"],
    group: "reading",
    icon: Layers,
    surface: "drawer",
    libraryTab: true,
  },
  {
    key: "tags",
    labelKey: "common:nav.tags",
    to: "/tags",
    minRole: "reader",
    matchPrefixes: ["/tags"],
    group: "reading",
    icon: Tag,
    surface: "drawer",
    libraryTab: true,
  },
  {
    key: "publishers",
    labelKey: "common:nav.publishers",
    to: "/publishers",
    minRole: "reader",
    matchPrefixes: ["/publishers"],
    group: "reading",
    icon: Building2,
    surface: "drawer",
    libraryTab: true,
  },
  {
    key: "suggestions",
    labelKey: "common:nav.suggestions",
    to: "/suggestions",
    minRole: "reader",
    matchPrefixes: ["/suggestions"],
    group: "reading",
    icon: Sparkles,
    surface: "drawer",
    libraryTab: true,
    feature: "authorSuggestions",
  },
  {
    key: "upload",
    labelKey: "common:nav.upload",
    to: "/upload",
    minRole: "editor",
    matchPrefixes: ["/upload"],
    group: "editing",
    icon: Upload,
  },
  {
    key: "inbox",
    labelKey: "common:nav.inbox",
    to: "/upload/inbox",
    minRole: "editor",
    matchPrefixes: ["/upload/inbox"],
    group: "editing",
    icon: Inbox,
    surface: "drawer",
  },
  {
    key: "administration",
    labelKey: "common:nav.administration",
    to: "/admin",
    minRole: "admin",
    matchPrefixes: ["/admin"],
    group: "admin",
    icon: Shield,
    surface: "inline",
  },
  {
    key: "admin-overview",
    labelKey: "admin:area.overview",
    to: "/admin",
    minRole: "admin",
    matchPrefixes: [],
    exact: true,
    group: "admin",
    icon: LayoutDashboard,
    surface: "drawer",
    adminPage: true,
  },
  {
    key: "admin-users",
    labelKey: "admin:area.users",
    to: "/admin/users",
    minRole: "admin",
    matchPrefixes: ["/admin/users"],
    group: "admin",
    icon: Users,
    surface: "drawer",
    adminPage: true,
  },
  {
    key: "admin-invitations",
    labelKey: "admin:area.invitations",
    to: "/admin/invitations",
    minRole: "admin",
    matchPrefixes: ["/admin/invitations"],
    group: "admin",
    icon: Mail,
    surface: "drawer",
    adminPage: true,
  },
  {
    key: "admin-jobs",
    labelKey: "admin:area.jobs",
    to: "/admin/jobs",
    minRole: "admin",
    matchPrefixes: ["/admin/jobs"],
    group: "admin",
    icon: ListChecks,
    surface: "drawer",
    adminPage: true,
  },
  {
    key: "admin-calibre",
    labelKey: "admin:area.calibre",
    to: "/admin/import/calibre",
    minRole: "admin",
    matchPrefixes: ["/admin/import"],
    group: "admin",
    icon: Import,
    surface: "drawer",
    adminPage: true,
  },
  {
    key: "admin-settings",
    labelKey: "admin:area.settings",
    to: "/admin/settings",
    minRole: "admin",
    matchPrefixes: ["/admin/settings"],
    group: "admin",
    icon: Database,
    surface: "drawer",
    adminPage: true,
  },
  {
    key: "admin-author-mapping",
    labelKey: "admin:area.authorMapping",
    to: "/admin/author-mapping",
    minRole: "admin",
    matchPrefixes: ["/admin/author-mapping"],
    group: "admin",
    icon: UserRoundSearch,
    surface: "drawer",
    adminPage: true,
  },
  {
    key: "admin-email",
    labelKey: "admin:area.email",
    to: "/admin/email",
    minRole: "admin",
    matchPrefixes: ["/admin/email"],
    group: "admin",
    icon: Send,
    surface: "drawer",
    adminPage: true,
  },
];

/**
 * Entries the role may see. Feature-gated entries need their flag in `features` (they stay hidden
 * while the flags are not loaded yet).
 */
export function visibleNavItems(
  role: string | null | undefined,
  features?: NavFeatures,
): NavItem[] {
  return NAV_ITEMS.filter(
    (item) =>
      hasRole({ role }, item.minRole) && (!item.feature || features?.[item.feature]?.enabled),
  );
}

/** Entries of the inline header navigation. */
export function inlineNavItems(role: string | null | undefined, features?: NavFeatures): NavItem[] {
  return visibleNavItems(role, features).filter((item) => item.surface !== "drawer");
}

/** Entries of the mobile drawer. */
export function drawerNavItems(role: string | null | undefined, features?: NavFeatures): NavItem[] {
  return visibleNavItems(role, features).filter((item) => item.surface !== "inline");
}

/** Admin pages the role may open, for the admin sidebar and the global search. */
export function adminPageItems(role: string | null | undefined, features?: NavFeatures): NavItem[] {
  return visibleNavItems(role, features).filter((item) => item.adminPage);
}

function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/** Active state in the header navigation: "Library" also covers book, author, series, tag and publisher pages. */
export function isNavItemActive(item: NavItem, pathname: string): boolean {
  if (item.exact && pathname === item.to) return true;
  return item.matchPrefixes.some((prefix) => matchesPrefix(pathname, prefix));
}

/** Active state in the drawer, where every entry is listed separately, so exactly one is current. */
export function isDrawerItemActive(item: NavItem, pathname: string): boolean {
  if (item.key === "library") return pathname === "/" || matchesPrefix(pathname, "/books");
  // "Upload" and "Inbox" are separate entries.
  if (item.key === "upload") return pathname === "/upload";
  return isNavItemActive(item, pathname);
}
